import { Box, Text, Button, useToast, Icon, Flex, Spinner, InlineNotification } from "@familyzone/component-library";
import { Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, useDisclosure } from "@chakra-ui/react";
import React, { useState, useEffect, useRef, useCallback } from "react";
import PageWithHeader from "../../templates/PageWithHeader";
import { useAcademyDeviceConfigStore } from "../../../storez/AcademyDeviceConfigStore";
import { getEmailTemplate, getEmailTemplateHtml } from "../../../utils/api/EmailTemplate";
import { getEmailTemplateEmlUrl } from "../../../utils/api/ContentApiConfig";
import Api from "../../../utils/Api";
import LayoutConfig from "../../../utils/LayoutConfig";
import { colors } from "../../../theme";

// Interface for the training item structure
interface TrainingItem {
  id: string;
  title: string;
  description: string;
  previewUrl?: string;
  courseImage?: string;
}

// Interface for the email template API response
interface ContentFieldValue {
  data?: string;
  image?: {
    contentUrl: string;
    title: string;
  };
}

interface NestedContentField {
  name: string;
  label: string;
  contentFieldValue: ContentFieldValue;
}

interface ContentField {
  name: string;
  label: string;
  contentFieldValue: ContentFieldValue;
  nestedContentFields?: NestedContentField[];
}

const ClasswizeTraining: React.FC = () => {
  const [trainingItems, setTrainingItems] = useState<TrainingItem[]>([]);
  const [templateData, setTemplateData] = useState<{
    description1?: string;
    description2?: string;
  }>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [previewLoading, setPreviewLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  // Add local state for device config when backend is not available
  const [localDeviceConfig, setLocalDeviceConfig] = useState<boolean>(false);
  // Add state for Classwize login URL
  const [loginUrl, setLoginUrl] = useState<string | null>(null);

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const deviceConfig = useAcademyDeviceConfigStore((state) => state.config);
  const fetchDeviceConfig = useAcademyDeviceConfigStore((state) => state.fetch);
  const saveDeviceConfig = useAcademyDeviceConfigStore((state) => state.save);

  const { errorToast, successToast } = useToast();

  // Helper function to get Classwize login URL
  const getClasswizeLoginUrl = (tenantKey?: string): string | null => {
    if (!tenantKey) {
      return null;
    }
    const baseUrl = (LayoutConfig as { classwize_link(): string }).classwize_link();
    return `${baseUrl}/login/${tenantKey}`;
  };

  // Helper function to fetch Classwize configuration and get login URL
  const fetchClasswizeLoginUrl = useCallback(async (): Promise<string | null> => {
    return new Promise((resolve) => {
      Api.get("/config/ajax/classwize", (result: { classwize: { tenant_key?: string } }) => {
        const loginUrl = getClasswizeLoginUrl(result.classwize.tenant_key);
        resolve(loginUrl);
      });
    });
  }, []);

  // Helper function to extract data from content fields
  const extractFieldData = (contentFields: ContentField[], fieldName: string): string | undefined => {
    const field = contentFields.find((f) => f.name === fieldName);
    return field?.contentFieldValue?.data;
  };

  // Helper function to extract course data
  const extractCourseData = (contentFields: ContentField[]): TrainingItem[] => {
    const courseFields = contentFields.filter((f) => f.name === "course");
    return courseFields.map((courseField, index) => {
      const nestedFields = courseField.nestedContentFields || [];
      const heading = nestedFields.find((f) => f.name === "courseHeading")?.contentFieldValue?.data || "";
      const courseId = nestedFields.find((f) => f.name === "courseId")?.contentFieldValue?.data || (index + 1).toString();
      const description = nestedFields.find((f) => f.name === "courseDescription")?.contentFieldValue?.data || "";
      const friendlyURL = nestedFields.find((f) => f.name === "friendlyURL")?.contentFieldValue?.data || "";
      const courseImage = nestedFields.find((f) => f.name === "courseImage")?.contentFieldValue?.image?.contentUrl || "";

      const cleanDescription = description;

      return {
        id: courseId,
        title: heading,
        description: cleanDescription,
        previewUrl: friendlyURL,
        courseImage: courseImage,
      };
    });
  };

  useEffect(() => {
    const fetchTrainingData = async () => {
      setLoading(true);
      try {
        // Fetch Classwize login URL
        const url = await fetchClasswizeLoginUrl();
        setLoginUrl(url);

        // Fetch email template data
        const template = await getEmailTemplate();
        const contentFields = template.contentFields || [];
        const description1 = extractFieldData(contentFields, "description1");
        const description2 = extractFieldData(contentFields, "description2");
        const courses = extractCourseData(contentFields);

        setTemplateData({ description1, description2 });
        setTrainingItems(courses);
      } catch (error) {
        console.error("Error fetching training data:", error);
        setError("Failed to load training data");
      } finally {
        setLoading(false);
      }
    };

    void fetchTrainingData();

    // Try to fetch device config, but don't fail if it doesn't work
    fetchDeviceConfig().catch((error) => {
      console.warn("Failed to fetch device config:", error);
      // Continue without device config
    });
  }, [fetchClasswizeLoginUrl, fetchDeviceConfig]);

  const handleEnableTraining = () => {
    setSaving(true);

    // Try to save to backend first
    const currentConfig = deviceConfig || { classwize_enabled: false };
    const newConfig = {
      ...currentConfig,
      classwize_enabled: true,
    };

    saveDeviceConfig(newConfig)
      .catch((error) => {
        console.error("Failed to save device config:", error);
        // If backend save fails, use local state
        setLocalDeviceConfig(true);
        successToast({
          title: "Access updated",
          description: "Access to Classroom Manager training has been updated successfully (local mode).",
          position: "top",
          isClosable: true,
        });
      })
      .finally(() => {
        setSaving(false);
        setIsSaved(true);
      });
  };

  const handlePreviewTemplate = async (friendlyURL: string) => {
    setPreviewLoading(friendlyURL);
    setPreviewHtml(null);
    onOpen();

    try {
      const html = await getEmailTemplateHtml(friendlyURL, loginUrl || undefined);
      setPreviewHtml(html);
    } catch (error) {
      console.error("Error fetching template preview:", error);
      errorToast({
        title: "Error loading preview",
        description: "Failed to load template preview. Please try again later.",
        position: "top",
        isClosable: true,
      });
      // Close modal on error
      onClose();
    } finally {
      setPreviewLoading(null);
    }
  };

  const handleDownloadTemplate = async (friendlyURL: string, title: string) => {
    try {
      const response = await fetch(getEmailTemplateEmlUrl(friendlyURL), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          loginUrl: loginUrl,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the blob from the response
      const blob = await response.blob();

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      // Ensure the URL is safe by using the blob URL directly
      link.href = url;
      // Sanitize the title to prevent XSS - only allow alphanumeric characters, hyphens, and underscores
      const sanitizedTitle = title.replace(/[^a-zA-Z0-9\-_]/g, "_").toLowerCase();
      link.download = `${sanitizedTitle}.eml`;
      // Use a temporary container to avoid direct body manipulation
      const tempContainer = document.createElement("div");
      tempContainer.style.display = "none";
      tempContainer.appendChild(link);
      document.body.appendChild(tempContainer);
      link.click();
      document.body.removeChild(tempContainer);
      window.URL.revokeObjectURL(url);

      successToast({
        title: "Template downloaded",
        description: "Email template has been downloaded successfully.",
        position: "top",
        isClosable: true,
      });
    } catch (error) {
      console.error("Error downloading template:", error);
      errorToast({
        title: "Error downloading template",
        description: "Failed to download template. Please try again later.",
        position: "top",
        isClosable: true,
      });
    }
  };

  const copyHtmlToClipboard = () => {
    if (!iframeRef.current) {
      return;
    }

    // Get the iframe document
    const iframeDoc = iframeRef.current.contentDocument;
    if (!iframeDoc) {
      return;
    }

    // Copy only the body content, not the entire HTML document
    const bodyElement = iframeDoc.body;
    if (!bodyElement) {
      return;
    }

    // Copy the body content without the document structure
    const htmlContent = bodyElement.innerHTML;

    // Copy HTML content to clipboard
    void copyToClipboard(htmlContent);
  };

  const copyToClipboard = async (htmlContent: string) => {
    try {
      const blob = new Blob([htmlContent], { type: "text/html" });
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": blob,
          "text/plain": new Blob([htmlContent.replace(/<[^>]*>/g, "")], { type: "text/plain" }),
        }),
      ]);
      successToast({
        title: "Template copied",
        description: "Email template has been copied to clipboard.",
        position: "top",
        isClosable: true,
      });
    } catch (err) {
      console.error("Copy failed", err);
      errorToast({
        title: "Error copying template",
        description: "Failed to copy template to clipboard. Please try again.",
        position: "top",
        isClosable: true,
      });
    }
  };

  const handleCloseModal = () => {
    setPreviewHtml(null);
    onClose();
  };

  const breadcrumbs = [
    { title: "Product Training", url: "/product-training", isActive: false },
    { title: "Classroom Manager Training", isActive: true },
  ];

  if (loading) {
    return (
      <PageWithHeader breadcrumbs={breadcrumbs} title="Classroom Manager Training">
        <Box sx={{ padding: "12px 24px 24px 24px", display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
          <Text>Loading training content...</Text>
        </Box>
      </PageWithHeader>
    );
  }

  if (error) {
    return (
      <PageWithHeader breadcrumbs={breadcrumbs} title="Classroom Manager Training">
        <Box sx={{ padding: "12px 24px 24px 24px", display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
          <Text sx={{ color: "#DC2626" }}>Error loading training content: {error}</Text>
        </Box>
      </PageWithHeader>
    );
  }

  return (
    <>
      <PageWithHeader breadcrumbs={breadcrumbs} title="Classroom Manager Training">
        <Flex p="24px" flexDirection="column">
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "24px",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                fontSize: "md",
                color: colors.neutrals["700"],
                margin: "0 0 30px 0",
                fontFamily: "roboto",
                "& h3": {
                  fontSize: "lg",
                  fontWeight: "600!important",
                },
                "& a": {
                  color: "#094C99",
                  fontWeight: "500",
                  "&:hover": {
                    color: "#23527C",
                    textDecoration: "underline",
                  },
                },
              }}
              dangerouslySetInnerHTML={{
                __html: templateData.description1 || "",
              }}
            />

            {!deviceConfig?.classwize_enabled && !localDeviceConfig && (
              <Flex
                bg={colors.accent.turquoise["100"]}
                px="12px"
                py="5px"
                borderRadius="8px"
                justifyContent="space-between"
                alignItems="center"
                mb="24px"
              >
                <Text fontSize="14px" fontWeight="500" color={colors.info.title}>
                  <Icon icon="fa-info-circle" mr="8px" width="16px" height="16px" color={colors.accent.turquoise["400"]} />
                  Please enable training before sending email communications to users to ensure a smooth rollout.
                </Text>
                <Button
                  variant="outline"
                  size="compact"
                  bg={colors.brand["100"]}
                  color="#006981"
                  borderColor="#69C9E7"
                  onClick={handleEnableTraining}
                  disabled={saving}
                  isLoading={saving}
                >
                  Enable training
                </Button>
              </Flex>
            )}

            {isSaved && (
              <Box mb="24px">
                <InlineNotification
                  status="success"
                  notificationDescription="Training is now accessible for all users of Classroom Manager."
                />
              </Box>
            )}

            <Box
              sx={{
                marginBottom: "24px",
                color: colors.neutrals["700"],
                fontFamily: "roboto",
                "& h3": {
                  fontSize: "18px",
                  fontWeight: "500",
                  marginBottom: "12px",
                  fontFamily: "poppins",
                },
                "& a": {
                  color: "#094C99",
                  fontWeight: "500",
                  "&:hover": {
                    color: "#23527C",
                    textDecoration: "underline",
                  },
                },
              }}
              dangerouslySetInnerHTML={{
                __html: templateData.description2 || "",
              }}
            />

            {trainingItems.length > 0 && (
              <Flex flexDirection="column" gap="16px">
                {trainingItems.map((item, index) => (
                  <Flex key={item.id} gap="24px" alignItems="center" position="relative">
                    {/* Email indicator - aligned with center of content box */}
                    <Flex flexDirection="column" alignItems="center" justifyContent="center" minWidth="120px" position="relative">
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <i className={`fa-regular fa-envelope`} style={{ fontSize: "32px", color: "#D2DAE5", marginBottom: "4px" }} />
                        <Text sx={{ fontSize: "12px", fontWeight: "500", color: "#68768B" }}>Email {index + 1}</Text>
                      </Box>
                    </Flex>

                    {/* Connecting line to next email - positioned relative to the row */}
                    {index < trainingItems.length - 1 && (
                      <Box
                        sx={{
                          position: "absolute",
                          left: "60px", // Center of email icon column (120px / 2)
                          top: "calc(50% + 30px)", // Start below current email icon
                          transform: "translateX(-50%)",
                          width: "2px",
                          bottom: "calc(-50% + 15px)",
                          borderLeft: "2px dashed #A0ADC0",
                          zIndex: 1,
                        }}
                      />
                    )}

                    {/* Content box */}
                    <Box
                      sx={{
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px",
                        padding: "16px",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "16px",
                        flex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: "116px",
                          height: "80px",
                          backgroundColor: "#E8F8EF",
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          overflow: "hidden",
                        }}
                      >
                        {item.courseImage ? (
                          <img
                            src={item.courseImage}
                            alt={item.title}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              objectPosition: "right",
                              borderRadius: "8px",
                            }}
                          />
                        ) : (
                          <i className={`fa fa-envelope`} style={{ fontSize: "24px", color: "#58B78E" }} />
                        )}
                      </Box>

                      <Box sx={{ flex: 1, my: "auto" }}>
                        <Text sx={{ fontSize: "18px", fontWeight: 500, color: colors.neutrals["700"], marginBottom: "8px" }}>
                          {item.title}
                        </Text>
                        <Box sx={{ fontSize: "14px", color: "#364255" }} dangerouslySetInnerHTML={{ __html: item.description }} />
                      </Box>

                      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flexShrink: 0 }}>
                        <Button
                          id="preview-template-button"
                          variant="outline"
                          size="sm"
                          leftIcon={<Icon icon="fa-eye" />}
                          onClick={() => item.previewUrl && void handlePreviewTemplate(item.previewUrl)}
                          disabled={previewLoading === item.previewUrl}
                          isLoading={previewLoading === item.previewUrl}
                        >
                          Preview template
                        </Button>
                        <Button
                          id="download-template-button"
                          variant="outline"
                          size="sm"
                          leftIcon={<Icon icon="fa-download" />}
                          onClick={() => item.previewUrl && void handleDownloadTemplate(item.previewUrl, item.title)}
                        >
                          Download template
                        </Button>
                      </Box>
                    </Box>
                  </Flex>
                ))}
              </Flex>
            )}

            {trainingItems.length === 0 && (
              <Box sx={{ padding: "24px", textAlign: "center" }}>
                <Text sx={{ fontSize: "16px", color: "#364255" }}>No training content available at the moment.</Text>
              </Box>
            )}
          </Box>
        </Flex>
      </PageWithHeader>
      <Modal size="lg" isOpen={isOpen} onClose={handleCloseModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton size="sm" marginLeft="auto" />
          <ModalHeader fontSize="18px" fontWeight="500" fontFamily="poppins">
            Preview Template
          </ModalHeader>
          <ModalBody>
            <Flex justifyContent="center" alignItems="center" height="737px" bg="#F1F5F9">
              {previewHtml ? (
                <iframe
                  ref={iframeRef}
                  srcDoc={previewHtml}
                  style={{
                    width: "750px",
                    height: "100%",
                    border: "1px solid #E2E8F0",
                    borderRadius: "4px",
                    backgroundColor: "white",
                  }}
                  title="Email Template Preview"
                />
              ) : (
                <Flex justifyContent="center" alignItems="center" height="100%">
                  <Spinner size="lg" />
                </Flex>
              )}
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Flex justifyContent="flex-end" gap="10px">
              <Button variant="outline" size="sm" onClick={() => handleCloseModal()}>
                Close
              </Button>
              <Button
                id="copy-template-button"
                variant="primary"
                size="sm"
                leftIcon={<Icon icon="fa-copy" />}
                onClick={() => void copyHtmlToClipboard()}
              >
                Copy template
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ClasswizeTraining;
